/**
 * DocsLibrary — TipTap documentation module main page.
 * Route: /documentation
 * Shows a card grid of docs with search, status/type filter and file upload.
 */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  DocumentTextIcon,
  TrashIcon,
  PencilSquareIcon,
  ArrowUpTrayIcon,
  LinkIcon,
  DocumentIcon,
  PhotoIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';

const STATUS_LABELS = {
  draft:     { label: 'Borrador',   bg: '#fef9c3', color: '#a16207' },
  published: { label: 'Publicado',  bg: '#dcfce7', color: '#15803d' },
};

const TYPE_ICONS = {
  richtext: { icon: '📝', label: 'Texto enriquecido' },
  file:     { icon: '📎', label: 'Archivo' },
  url:      { icon: '🔗', label: 'Enlace externo' },
};

function formatFileSize(bytes) {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function DocsLibrary({ companyId }) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [docs,    setDocs]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [search,  setSearch]  = useState('');
  const [statusF, setStatusF] = useState('');
  const [typeF,   setTypeF]   = useState('');
  const [tagF,    setTagF]    = useState('');
  const [uploading, setUploading] = useState(false);

  // URL modal
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [urlForm, setUrlForm] = useState({ title: '', external_url: '', description: '' });

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true); setError('');
    try {
      const list = await api.getDocPages(companyId, { q: search, status: statusF, tag: tagF, doc_type: typeF });
      setDocs(list);
    } catch (err) {
      setError(err.message || 'Error cargando documentos');
    } finally { setLoading(false); }
  }, [companyId, search, statusF, tagF, typeF]);

  useEffect(() => { load(); }, [load]);

  // Collect all tags for the tag filter dropdown
  const allTags = useMemo(() => {
    const set = new Set();
    docs.forEach(d => (d.tags || []).forEach(t => set.add(t)));
    return [...set].sort();
  }, [docs]);

  const handleDelete = async (doc, e) => {
    e.stopPropagation();
    if (!window.confirm(`¿Eliminar el documento "${doc.title}"?`)) return;
    try {
      await api.deleteDocPage(doc.id, companyId);
      load();
    } catch (err) {
      alert(err.message || 'No se pudo eliminar');
    }
  };

  // File upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setError('');
    try {
      await api.uploadDocFile(companyId, file);
      load();
    } catch (err) {
      setError(err.message || 'Error al subir archivo');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // URL doc creation
  const handleCreateUrl = async () => {
    if (!urlForm.title.trim() || !urlForm.external_url.trim()) {
      alert('Título y URL son obligatorios');
      return;
    }
    try {
      await api.createDocPage(companyId, {
        title: urlForm.title.trim(),
        description: urlForm.description.trim() || null,
        doc_type: 'url',
        external_url: urlForm.external_url.trim(),
        status: 'published',
      });
      setShowUrlModal(false);
      setUrlForm({ title: '', external_url: '', description: '' });
      load();
    } catch (err) {
      alert(err.message || 'Error al crear enlace');
    }
  };

  const handleCardClick = (doc) => {
    if (doc.doc_type === 'url' && doc.external_url) {
      window.open(doc.external_url, '_blank');
    } else if (doc.doc_type === 'file') {
      // Navigate to a viewer/download — for now just download
      navigate(`/documentation/${doc.id}`);
    } else {
      navigate(`/documentation/${doc.id}`);
    }
  };

  return (
    <div style={{ padding: 'clamp(0.85rem, 3vw, 2rem)', maxWidth: 1200, margin: '0 auto' }}>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem', marginBottom: '1.5rem' }}>
        <div style={{ flex: '1 1 220px', minWidth: 0 }}>
          <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
            📚 Documentación
          </h1>
          <p style={{ color: 'var(--text-secondary)', margin: '0.25rem 0 0', fontSize: '0.88rem' }}>
            Crea, sube y vincula documentos con cualquier elemento de la infraestructura.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setShowUrlModal(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.3rem',
              background: 'var(--bg-toolbar)', color: 'var(--text-secondary)', border: '1px solid var(--border)',
              borderRadius: 6, padding: '0.5rem 0.85rem',
              fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
            }}
          >
            <LinkIcon style={{ width: 15, height: 15 }} />
            Enlace
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.3rem',
              background: 'var(--bg-toolbar)', color: 'var(--text-secondary)', border: '1px solid var(--border)',
              borderRadius: 6, padding: '0.5rem 0.85rem',
              fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer',
            }}
          >
            <ArrowUpTrayIcon style={{ width: 15, height: 15 }} />
            {uploading ? 'Subiendo…' : 'Subir archivo'}
          </button>
          <input ref={fileInputRef} type="file" hidden onChange={handleFileUpload} />
          <button
            onClick={() => navigate('/documentation/new')}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              background: '#2563eb', color: '#fff', border: 'none',
              borderRadius: 6, padding: '0.5rem 1rem',
              fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer',
            }}
          >
            <PlusIcon style={{ width: 16, height: 16 }} />
            Nuevo documento
          </button>
        </div>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 220px' }}>
          <MagnifyingGlassIcon style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            width: 15, height: 15, color: '#94a3b8',
          }} />
          <input
            type="text"
            placeholder="Buscar por título o descripción…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              paddingLeft: 30, width: '100%', border: '1px solid #cbd5e1',
              borderRadius: 6, padding: '0.42rem 0.65rem 0.42rem 2rem',
              fontSize: '0.85rem', outline: 'none',
            }}
          />
        </div>
        <select
          value={statusF}
          onChange={e => setStatusF(e.target.value)}
          style={{ border: '1px solid #cbd5e1', borderRadius: 6, padding: '0.42rem 0.65rem', fontSize: '0.85rem', minWidth: 130 }}
        >
          <option value="">Todos los estados</option>
          <option value="draft">Borrador</option>
          <option value="published">Publicado</option>
        </select>
        <select
          value={typeF}
          onChange={e => setTypeF(e.target.value)}
          style={{ border: '1px solid #cbd5e1', borderRadius: 6, padding: '0.42rem 0.65rem', fontSize: '0.85rem', minWidth: 130 }}
        >
          <option value="">Todos los tipos</option>
          <option value="richtext">📝 Texto enriquecido</option>
          <option value="file">📎 Archivo</option>
          <option value="url">🔗 Enlace</option>
        </select>
        {allTags.length > 0 && (
          <select
            value={tagF}
            onChange={e => setTagF(e.target.value)}
            style={{ border: '1px solid #cbd5e1', borderRadius: 6, padding: '0.42rem 0.65rem', fontSize: '0.85rem', minWidth: 130 }}
          >
            <option value="">Todas las etiquetas</option>
            {allTags.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
      </div>

      {/* ── Error / Empty / Loading ── */}
      {error && (
        <div style={{ background: 'rgba(239,68,68,.15)', border: '1px solid rgba(239,68,68,.4)', borderRadius: 6, padding: '0.75rem 1rem', color: '#fca5a5', marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {loading && (
        <div style={{ textAlign: 'center', padding: '4rem', color: '#94a3b8' }}>Cargando…</div>
      )}

      {!loading && !error && docs.length === 0 && (
        <div style={{
          textAlign: 'center', padding: '5rem 2rem',
          background: 'var(--bg-toolbar)', borderRadius: 12, border: '2px dashed var(--border)',
        }}>
          <DocumentTextIcon style={{ width: 48, height: 48, color: '#cbd5e1', margin: '0 auto 1rem' }} />
          <p style={{ color: '#94a3b8', fontWeight: 600, fontSize: '1rem' }}>
            {search || statusF || tagF || typeF ? 'No hay documentos que coincidan con los filtros' : 'Aún no hay documentos'}
          </p>
          {!search && !statusF && !tagF && !typeF && (
            <button
              onClick={() => navigate('/documentation/new')}
              style={{
                marginTop: '1rem', background: '#2563eb', color: '#fff',
                border: 'none', borderRadius: 6, padding: '0.5rem 1.2rem',
                fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer',
              }}
            >
              Crear primer documento
            </button>
          )}
        </div>
      )}

      {/* ── Card Grid ── */}
      {!loading && docs.length > 0 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: '1rem',
        }}>
          {docs.map(doc => {
            const st = STATUS_LABELS[doc.status] || STATUS_LABELS.draft;
            const typeInfo = TYPE_ICONS[doc.doc_type] || TYPE_ICONS.richtext;
            const isImage = doc.mime_type && doc.mime_type.startsWith('image/');
            const isPdf = doc.mime_type === 'application/pdf';

            return (
              <div
                key={doc.id}
                onClick={() => handleCardClick(doc)}
                style={{
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  color: 'var(--text-primary)',
                  transition: 'box-shadow 0.15s, border-color 0.15s',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.4)';
                  e.currentTarget.style.borderColor = 'rgba(255,255,255,.2)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.3)';
                  e.currentTarget.style.borderColor = 'var(--border)';
                }}
              >
                {/* Preview area */}
                <div style={{
                  height: 100, background: 'var(--bg-toolbar)',
                  borderBottom: '1px solid var(--border)', overflow: 'hidden',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  {doc.doc_type === 'file' && isImage ? (
                    <img
                      src={`/api/docs/${doc.id}/download?inline=true&company_id=${companyId}`}
                      alt={doc.title}
                      style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
                      onError={e => { e.target.style.display = 'none'; }}
                    />
                  ) : doc.doc_type === 'file' ? (
                    <div style={{ textAlign: 'center' }}>
                      <DocumentIcon style={{ width: 32, height: 32, color: 'var(--text-muted)' }} />
                      <p style={{ fontSize: '0.68rem', color: 'var(--text-muted)', margin: '0.2rem 0 0' }}>
                        {doc.original_name || 'Archivo'}
                      </p>
                      {doc.file_size && (
                        <p style={{ fontSize: '0.62rem', color: 'var(--text-secondary)', margin: 0 }}>{formatFileSize(doc.file_size)}</p>
                      )}
                    </div>
                  ) : doc.doc_type === 'url' ? (
                    <div style={{ textAlign: 'center' }}>
                      <LinkIcon style={{ width: 32, height: 32, color: 'var(--accent-l)' }} />
                      <p style={{ fontSize: '0.68rem', color: 'var(--text-muted)', margin: '0.2rem 0 0', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {doc.external_url || 'URL'}
                      </p>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center' }}>
                      <DocumentTextIcon style={{ width: 32, height: 32, color: 'var(--text-secondary)' }} />
                      <p style={{ fontSize: '0.68rem', color: 'var(--text-muted)', margin: '0.2rem 0 0' }}>Texto enriquecido</p>
                    </div>
                  )}
                </div>

                {/* Card body */}
                <div style={{ padding: '0.85rem' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', minWidth: 0 }}>
                      <span style={{ fontSize: '0.85rem' }}>{typeInfo.icon}</span>
                      <h3 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 700, color: 'var(--text-primary)', lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {doc.title}
                      </h3>
                    </div>
                    <span style={{
                      background: st.bg, color: st.color,
                      fontSize: '0.68rem', fontWeight: 700,
                      padding: '0.15rem 0.5rem', borderRadius: 99,
                      whiteSpace: 'nowrap', flexShrink: 0,
                    }}>
                      {st.label}
                    </span>
                  </div>
                  {doc.description && (
                    <p style={{
                      margin: '0 0 0.5rem', fontSize: '0.8rem', color: 'var(--text-secondary)',
                      overflow: 'hidden', display: '-webkit-box',
                      WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                    }}>
                      {doc.description}
                    </p>
                  )}

                  {/* Tags */}
                  {(doc.tags || []).length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem', marginBottom: '0.5rem' }}>
                      {(doc.tags || []).slice(0, 4).map(tag => (
                        <span key={tag} style={{
                          background: 'rgba(59,130,246,.16)', color: '#93c5fd',
                          fontSize: '0.68rem', fontWeight: 600,
                          padding: '0.1rem 0.45rem', borderRadius: 4,
                        }}>
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Footer */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '0.5rem' }}>
                    <span style={{ fontSize: '0.72rem', color: '#94a3b8' }}>
                      {doc.link_count > 0 && (
                        <span style={{ marginRight: 8 }}>🔗 {doc.link_count} {doc.link_count === 1 ? 'enlace' : 'enlaces'}</span>
                      )}
                      {doc.latest_version > 0 && `v${doc.latest_version}`}
                    </span>
                    <div style={{ display: 'flex', gap: '0.3rem' }}>
                      {doc.doc_type === 'file' && (
                        <button
                          title="Descargar"
                          onClick={e => {
                            e.stopPropagation();
                            api.downloadAuthedFile(
                              `/api/docs/${doc.id}/download?company_id=${companyId}`,
                              doc.original_name || doc.title || 'documento'
                            ).catch(err => alert(err.message || 'No se pudo descargar el documento'));
                          }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', color: '#2563eb', borderRadius: 4 }}
                        >
                          <ArrowDownTrayIcon style={{ width: 15, height: 15 }} />
                        </button>
                      )}
                      {doc.doc_type === 'richtext' && (
                        <button
                          title="Editar"
                          onClick={e => { e.stopPropagation(); navigate(`/documentation/${doc.id}`); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', color: 'var(--text-secondary)', borderRadius: 4 }}
                        >
                          <PencilSquareIcon style={{ width: 15, height: 15 }} />
                        </button>
                      )}
                      <button
                        title="Eliminar"
                        onClick={e => handleDelete(doc, e)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', color: '#dc2626', borderRadius: 4 }}
                      >
                        <TrashIcon style={{ width: 15, height: 15 }} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── URL Modal ── */}
      {showUrlModal && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
            zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
          onClick={() => setShowUrlModal(false)}
        >
          <div
            style={{
                background: 'var(--bg-surface)', borderRadius: 10, width: 420, maxWidth: '95vw',
              padding: '1.5rem', border: '1px solid var(--border)', color: 'var(--text-primary)',
              boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
              🔗 Añadir enlace externo
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.65rem' }}>
              <input
                style={modalInputStyle}
                placeholder="Título *"
                value={urlForm.title}
                onChange={e => setUrlForm(p => ({ ...p, title: e.target.value }))}
                autoFocus
              />
              <input
                style={modalInputStyle}
                placeholder="https://…"
                value={urlForm.external_url}
                onChange={e => setUrlForm(p => ({ ...p, external_url: e.target.value }))}
              />
              <input
                style={modalInputStyle}
                placeholder="Descripción (opcional)"
                value={urlForm.description}
                onChange={e => setUrlForm(p => ({ ...p, description: e.target.value }))}
              />
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                <button
                  onClick={handleCreateUrl}
                  style={{
                    flex: 1, padding: '0.5rem', border: 'none', borderRadius: 6,
                    background: '#2563eb', color: '#fff', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer',
                  }}
                >
                  Crear enlace
                </button>
                <button
                  onClick={() => setShowUrlModal(false)}
                  style={{
                    padding: '0.5rem 1rem', border: '1px solid var(--border)', borderRadius: 6,
                    background: 'var(--bg-toolbar)', color: 'var(--text-secondary)', fontSize: '0.88rem', cursor: 'pointer',
                  }}
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const modalInputStyle = {
  width: '100%', padding: '0.5rem 0.75rem',
  border: '1px solid var(--border)', borderRadius: 6,
  fontSize: '0.88rem', outline: 'none', fontFamily: 'inherit',
  background: 'var(--bg-input)', color: 'var(--text-primary)',
};
